import React from 'react';
import { Circle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NextCircleSolid } from '@tonerow/teenyicons-react';
import type { Passport, DigitalPassport, Pathway, Skillstree } from '@/types/achievement';

interface AvailablePathwaysProps {
  filteredPathways?: (Passport|Pathway|Skillstree & { associated_passport?: DigitalPassport })[];
  onPassportClick?: (passport: Passport|DigitalPassport) => void;
}

const AvailablePathways: React.FC<AvailablePathwaysProps> = ({ 
  filteredPathways,
  onPassportClick,
}) => {
  return (
    <div>
      <h3 className="flex flex-row text-base font-normal text-white/90 mb-4">                
        <Circle size={18} className="text-white/60 mt-1 me-2 mb-4" />
        Available Role Pathways
      </h3>
      {filteredPathways && filteredPathways.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPathways.map((pathway, index) => (
            <PathwayCard 
              key={index} 
              pathway={pathway} 
            />
          ))}
        </div>
      ) : (
        <span className="text-white/60">There are no ongoing pathways found</span>
      )}
    </div>
  );
};

interface PathwayCardProps {
  pathway: Passport|DigitalPassport|Pathway & { associated_passport?: Passport }|Skillstree & { associated_passport?: Passport };
  onPassportClick?: (passport: Passport|DigitalPassport|Pathway) => void;
}

const PathwayCard: React.FC<PathwayCardProps> = ({ pathway, onPassportClick }) => {
  const navigate = useNavigate();
  // Calculate progress based on associated passport badges and skillstree micro skills
  const calculateProgressData = () => {
    let totalMicroskills = 0;
    let completedMicroskills = 0;
    
    // Get total micro skills from the skillstree version
    const version = (pathway as Skillstree).versions?.[0];
    if (version?.micro_skills) {
      totalMicroskills = version.micro_skills.length;
    }
    
    // Get completed badges from associated passport
    if ((pathway as Skillstree).associated_passport) {
      completedMicroskills = (pathway as Skillstree).associated_passport.statistics?.valid_badges || 0;
    }
    
    return { totalMicroskills, completedMicroskills };
  };

  const { totalMicroskills, completedMicroskills } = calculateProgressData();
  
  // Calculate progress percentage
  const calculateProgressPercentage = () => {
    if (totalMicroskills === 0) return 0;
    return Math.round((completedMicroskills / totalMicroskills) * 100);
  };

  const percentage = calculateProgressPercentage();
  const remainingPercentage = Math.max(0, 100 - percentage);
  
  // console.log('pathway data:', pathway);
  // console.log('microskills count:', completedMicroskills, 'total:', totalMicroskills, 'percentage:', percentage);
  
  const role = (pathway as Passport).role || (pathway as Skillstree).skillstree_name || ((pathway as Passport).skillstree as Skillstree)?.name || 'Unknown';
  const image = (pathway as Passport).image || (pathway as Skillstree)?.image_url || ((pathway as Passport).skillstree as Skillstree)?.image_url || "/pages/achievements/skilltrue-pathway.png";
  const field = (pathway as Passport).field || (pathway as Passport).category || (pathway as Skillstree).core_category_name || (pathway as Skillstree).category_name || null;
  const microskillsDisplay = totalMicroskills > 0 ? `${completedMicroskills}/${totalMicroskills}` : `${completedMicroskills}`;
  
  const handleClick = () => {
    if ((pathway as Skillstree).associated_passport) {
      if (onPassportClick) {
        onPassportClick((pathway as Skillstree).associated_passport);
      } else {
        let path: string = `/skillstree`;
        let categoryId = (pathway as Skillstree)?.core_category_id;
        let skillstreeId = (pathway as Skillstree)?.skillstree_id;
        if (categoryId || categoryId !== 'undefined') {
          path = `${path}/${categoryId}`;
          if (skillstreeId) {
            path = `${path}/${skillstreeId}`;
          }
        }
        navigate(path);
      }
    }
  };  
  return (
    <div 
      className="bg-white/30 border border-white/20 rounded-2xl p-6 space-y-4 cursor-pointer hover:bg-white/40 transition-colors"
      onClick={handleClick}
    >
      <div className="flex sm:flex-row sm:justify-between flex-col justify-center items-center">
        <img 
          className="flex h-auto max-w-16 min-w-16 object-center" 
          src={image} 
          alt={role} 
          onError={(e) => e.currentTarget.src = '/pages/achievements/skilltrue-passport.png'}
        />
        <div className="flex flex-grow flex-col sm:items-start items-center ps-3">
          <span className="text-lg font-normal text-white mb-1">{role}</span>
          {field && (
            <span className="text-sm font-medium bg-purple-100 text-purple-800 rounded-sm mt-1 py-0.5 px-2.5">
              {field}
            </span>
          )}
        </div>
      </div>
      
      <p className="text-white text-sm mb-4">
        {(pathway as Pathway).description || 'Develop your skills and earn this role passport'}
      </p>
      
      <div className="flex flex-col flex-wrap gap-2">
        <div className="flex flex-row justify-between text-white/60 text-sm">
          <span>Progress</span>
          <span>{microskillsDisplay} badges</span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-full rounded-full bg-gradient-to-r from-blue-500 to-purple-500`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
        
        <div className="flex flex-row justify-between text-white text-sm mt-3">
          <span>{remainingPercentage}% remaining</span>
          <NextCircleSolid className="size-6 -mt-2" />
        </div>
      </div>
    </div>
  );
};

export default AvailablePathways;